import { type ShouldRevalidateFunctionArgs } from 'react-router';

import { useAppConfig } from '#/config/AppConfigContext';
import { RouteModule } from '#/horizon/components/RouteModule';
import { cmsApiIsomorphicFetchPage } from '#/modules/cms/api/cmsApiFetchPage';
import { CmsContentPageLoadable } from '#/modules/cms/containers/ContentPage/CmsContentPageLoadable';
import { CmsContentPageAssignLocation } from '#/modules/cms/containers/ContentPageAssignLocation/CmsContentPageAssignLocation';
import { CmsContentPageReplaceLocation } from '#/modules/cms/containers/ContentPageReplaceLocation/CmsContentPageReplaceLocation';
import { getCmsSearchLandingPagePageLayoutHandleProps } from '#/modules/cms/helpers/getCmsSearchLandingPagePageLayoutHandleProps';
import { CmsPagePageLoadable } from '#/modules/cms/pages/Page/CmsPagePageLoadable';
import { getCmsPagePageLayoutHandleProps } from '#/modules/cms/pages/Page/getCmsPagePageLayoutHandleProps';
import { CmsPostPageLoadable } from '#/modules/cms/pages/Post/CmsPostPageLoadable';
import { getCmsPagePostLayoutHandleProps } from '#/modules/cms/pages/Post/getCmsPagePostLayoutHandleProps';
import { getCmsContentOgImage } from '#/modules/cms/pages/splat/getCmsContentOgImage';
import { PostSearchPageLoadable } from '#/modules/postSearch/pages/PostSearch/PostSearchPageLoadable';
import { getPostSearchPageLayoutHandleProps } from '#/modules/postSearch/pages/PostSearch/getPostSearchPageLayoutHandleProps';
import { SearchLandingPageLoadable } from '#/modules/search/pages/Landing/SearchLandingPageLoadable';
import { NotFoundPageLoadable } from '#/pages/NotFound/NotFoundPageLoadable';
import { HORIZON_ENABLED } from '#/rendering/state/renderingState';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import type { HorizonLoaderFunctionArgs } from '#/routing/types/HorizonLoaderFunctionArgs';
import { idealistNotFound } from '#/routing/utils/idealistNotFound';
import { idealistRedirect } from '#/routing/utils/idealistRedirect';
import { imageUrlForOgMeta } from '#/utils/filestack';

import type { CmsSplatPageLoaderData } from './CmsSplatPageLoaderData';
import { cmsSplatRouteRevalidation } from './cmsSplatRouteRevalidation';

export const cmsRouteId = 'cms-route';
export const id = cmsRouteId; // only used on hypernova

// Only revalidate if the pathname changes or `?pq=` is added or removed
export function shouldRevalidate({
  currentUrl,
  nextUrl,
}: ShouldRevalidateFunctionArgs): boolean {
  if (cmsSplatRouteRevalidation.force) return true;

  if (currentUrl.pathname !== nextUrl.pathname) return true;
  if (currentUrl.searchParams.has('pq') !== nextUrl.searchParams.has('pq'))
    return true;

  return false;
}

export async function loader({
  request,
  context,
  params,
}: HorizonLoaderFunctionArgs<Record<'*', string>>) {
  const slug = params['*'];

  const { searchParams } = new URL(request.url);

  try {
    const { data: content, ssrLandingPageResults } =
      await cmsApiIsomorphicFetchPage({
        request,
        context,
      })(slug, searchParams.get('preview') === 'true');

    if (content.type === 'CMS_REDIRECT') {
      if (HORIZON_ENABLED)
        return idealistRedirect({ request, context })(content.url);
      return {
        type: 'CONTENT_REPLACE_LOCATION',
        url: content.url,
      } satisfies CmsSplatPageLoaderData;
    }

    // FREE_CODE blocks with <script> tags won't be properly executed in
    // dynamically loaded pages due to browser security restrictions, so we need
    // to reload the page in that case. Previous discussion:
    // https://idealist.slack.com/archives/CHJ9JK7TP/p1730383641821849
    if (
      typeof global.window !== 'undefined' &&
      'contentBlocks' in content &&
      content.contentBlocks?.some((block) => block.type === 'FREE_CODE')
    ) {
      const requestUrl = new URL(request.url);
      const url = requestUrl.pathname + requestUrl.search + requestUrl.hash;

      return {
        type: 'CONTENT_ASSIGN_LOCATION',
        url,
      } satisfies CmsSplatPageLoaderData;
    }

    if (content.type === 'SEARCH_LANDING_PAGE') {
      await SearchLandingPageLoadable.load();
      return {
        type: 'SEARCH_LANDING_PAGE',
        content,
        ssr: { results: ssrLandingPageResults },
        handle: getCmsSearchLandingPagePageLayoutHandleProps({ content }),
      } satisfies CmsSplatPageLoaderData;
    }

    if (searchParams.has('pq') && content.subsite.homePage) {
      await PostSearchPageLoadable.load();
      return {
        type: 'POST_SEARCH',
        content,
        handle: getPostSearchPageLayoutHandleProps({ content }),
      } satisfies CmsSplatPageLoaderData;
    }

    if (content.type === 'PAGE') {
      await Promise.all([
        CmsContentPageLoadable.load(),
        CmsPagePageLoadable.load(),
      ]);

      return {
        type: 'CONTENT',
        content,
        handle: getCmsPagePageLayoutHandleProps({ content }),
      } satisfies CmsSplatPageLoaderData;
    }

    // content.type === 'POST'
    await Promise.all([
      CmsContentPageLoadable.load(),
      CmsPostPageLoadable.load(),
    ]);

    return {
      type: 'CONTENT',
      content,
      handle: getCmsPagePostLayoutHandleProps({ content }),
    } satisfies CmsSplatPageLoaderData;
  } catch {
    try {
      await NotFoundPageLoadable.load();
    } catch {}

    return idealistNotFound(null);
  }
}

export default function CmsSplatHorizonRoute({
  loaderData,
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { appConfig } = useAppConfig();

  switch (loaderData?.type) {
    case undefined:
      return <NotFoundPageLoadable noLayout />;
    case 'CONTENT_REPLACE_LOCATION':
      return <CmsContentPageReplaceLocation url={loaderData.url} />;
    case 'CONTENT_ASSIGN_LOCATION':
      return <CmsContentPageAssignLocation url={loaderData.url} />;
    case 'CONTENT': {
      const image = getCmsContentOgImage(loaderData.content);

      return (
        <RouteModule
          title={loaderData.content.headTitle ?? loaderData.content.title}
          ogImage={
            image
              ? { url: imageUrlForOgMeta(image), alt: image.altText ?? null }
              : null
          }
          description={loaderData.content.metaDescription}
          rssFeed={
            loaderData.content.subsite.homePage?.id === loaderData.content.id
              ? {
                  title: `${loaderData.content.headTitle ?? loaderData.content.title} Feed`,
                  href: `${appConfig.idealist.appUrl}/${loaderData.content.url}/feed.rss`,
                }
              : null
          }
          robotsNoIndex={!loaderData.content.visibleToRobots}
          article={
            loaderData.content.type === 'POST'
              ? {
                  firstPublished: loaderData.content.articleMeta.firstPublished,
                  lastModified: loaderData.content.articleMeta.lastModified,
                  author: loaderData.content.author?.name ?? null,
                  tags: loaderData.content.tags.map((tag) => tag.tag),
                }
              : null
          }
          translations={{
            items: [loaderData.content, ...loaderData.content.translations].map(
              (translation) => ({
                url: `${appConfig.idealist.appUrl}/${translation.url}`,
                locale: translation.locale,
              }),
            ),
            xDefault: loaderData.content.xDefaultTranslationUrl,
          }}
          schemaMarkup={
            loaderData.content.type === 'POST'
              ? loaderData.content.schemaMarkup
              : null
          }
        >
          <CmsContentPageLoadable content={loaderData.content} />
        </RouteModule>
      );
    }
    case 'SEARCH_LANDING_PAGE':
      return (
        <RouteModule
          title={
            loaderData.content.location?.name
              ? `${loaderData.content.name}, ${loaderData.content.location.name}`
              : loaderData.content.name
          }
          description={loaderData.content.description}
          translations={
            loaderData.content.translations.length > 0
              ? {
                  items: loaderData.content.translations.map((translation) => ({
                    url: `${appConfig.idealist.appUrl}/${translation.url}`,
                    locale: translation.locale,
                  })),
                  xDefault: `${appConfig.idealist.appUrl}/${loaderData.content.translations[0].url}`,
                }
              : null
          }
        >
          <SearchLandingPageLoadable content={loaderData.content} />
        </RouteModule>
      );
    case 'POST_SEARCH':
      return (
        <RouteModule
          title={loaderData.content.headTitle ?? loaderData.content.title}
          description={loaderData.content.metaDescription}
        >
          <PostSearchPageLoadable content={loaderData.content} />
        </RouteModule>
      );
  }
}
